// TODUE V2 — SQLite engine (driver abstrakcia).
// SSOT prepis: Dexie/IndexedDB → SQLite. Jedno rozhranie `SqlEngine` za ktorým
// sú 3 drivery podľa platformy:
//   • web/desktop → wa-sqlite (WASM/OPFS) cez @subframe7536/sqlite-wasm
//   • mobil       → @capacitor-community/sqlite (natívny iOS/Android)
//   • testy/node  → better-sqlite3 (:memory:, synchronný) alebo node:sqlite fallback
//
// Engine je TENKÁ vrstva — len primitíva exec/run/all/get/transaction. JSON
// (de)serializáciu, soft-delete, LWW a kernel markery rieši adapter.js nad ním.
//
// LAZY: žiadny driver sa neimportuje na úrovni modulu (jsdom by spadol na WASM).
// DB sa otvorí až prvým `await engine()` / `engine.open()`.
//
// Spec: MasterProject/workflows/todue-v2-mythos/SQLITE-MIGRATION-SPEC.md §2

// ─── Mutex pre top-level transakcie ──────────────────────────────────────────
// Jedna SQLite connection nezvláda dve PARALELNÉ top-level transakcie naraz:
// druhý `BEGIN IMMEDIATE` by buď spadol ('cannot start a transaction within a
// transaction'), alebo by sa dva čítače faktúr prečítali rovnaký riadok →
// duplicitné číslo. Serializujeme preto top-level vstupy cez promise-chain lock.
//
// DÔLEŽITÉ (race fix): "vnorenosť" sa NEROZHODUJE z globálneho countera. Globálny
// txDepth čítaný PRED mutexom bol chybný — pri async fn (await vnútri tx) sa event
// loop pustí kým txDepth=1, druhá NEZÁVISLÁ top-level tx ho prečíta ako >0 a chybne
// vbehne do SAVEPOINT vetvy na cudzej transakcii → "no such savepoint" / lost update.
//
// Korektný model: tx-scoped handle. `transaction(fn)` vždy serializuje cez mutex
// (BEGIN IMMEDIATE…COMMIT) a fn dostane `txApi`, ktorého `.transaction()` robí
// SAVEPOINT priamo (žiadny mutex → žiadny self-deadlock). Reentrancia je teda viazaná
// na to že volajúci DRŽÍ txApi (synchrónny reťazec aktívnej tx), nie na globálny stav.
function createTxMutex() {
  let tail = Promise.resolve();
  // Zaradí `task` (vracajúci Promise) na koniec reťaze. Ďalší task čaká kým
  // predošlý dobehne (úspech aj chyba), ale chybu predošlého neprepíše.
  return function runExclusive(task) {
    const result = tail.then(task, task);
    // tail nesmie "zomrieť" na chybe tasku — drž ho ako settled (catch→ignore),
    // inak by ďalší task v reťazi dostal odmietnutie predošlého.
    tail = result.then(() => {}, () => {});
    return result;
  };
}

// ─── Tx mechanizmus (spoločný pre všetky drivery) ─────────────────────────────
// Dostane `exec` (sync alebo async) + builder ktorý vyrobí api objekt pre danú
// tx-scope. Vráti top-level `transaction(fn)` na pripojenie do hlavného `api`.
//
// Model:
//   • top-level `transaction(fn)`  → mutex → BEGIN IMMEDIATE … COMMIT.
//       fn dostane `txApi` = makeApiForExec(txTransaction), kde txTransaction
//       robí SAVEPOINT (vnorené operácie cez `tx.transaction(...)`).
//   • savepoint `transaction(fn)`  → žiadny mutex (beží na už zamknutej conn),
//       SAVEPOINT/RELEASE/ROLLBACK TO; depth lokálne v closure (nie globálne).
//
// makeApiForExec(transactionFn) → vytvorí { exec,run,all,get,transaction,… } so
// zadaným `transaction`. Top-level a každá tx-scope dostanú vlastnú referenciu.
//
// Vracia { transaction, runExclusive }: `runExclusive` je ZDIEĽANÝ mutex — okrem
// top-level `transaction()` cez neho MUSIA prejsť aj top-level plain primitíva
// (run/all/get/exec) v makeApi, inak by sa za súbehu vlepili do cudzej otvorenej
// BEGIN IMMEDIATE tx na tej istej connection (P1-ADAPTER-TX-MUTEX: falošné done /
// lost update / near-duplikát). Tx-scoped primitíva NEsmú cez mutex (self-deadlock).
// Self-deadlock watchdog (Direction A poistka). Pod kontraktom sa vnútri
// db.transaction(cb) používa tx handle (cb ho dostane), NIKDY globálny db ani
// ambientná db.transaction. Porušenie = operácia čaká na engine mutex, ktorý drží
// bežiaca tx; ak je volajúci NA jej async reťazci, tx nikdy nezakomituje →
// self-deadlock. To sa nedá lexikálne odlíšiť od legitímneho nezávislého op
// (ten len počká ~ms). Ak op/tx visí > prah → skoro isto deadlock: vypíš
// diagnostiku so stackom namiesto nemého timeoutu. Warn-only — žiadny throw ani
// reject: naivné odmietnutie by nechalo zaradený mutex task predsa dobehnúť po
// rollbacku cudzej tx → zombie zápis po tom, čo volajúci už videl chybu. Prah je
// 30 s v prod (veľký OPFS import legitímne trvá) / 2 s v teste (pod Jest timeout).
function makeDeadlockWatchdog(isTest) {
  const WATCHDOG_MS = isTest ? 2000 : 30000;
  return function arm(what) {
    const { stack } = new Error(`[v2/engine] ${what} enqueued while a transaction is open`);
    const timer = setTimeout(() => {
      console.error(
        '[v2/engine] MOŽNÝ self-deadlock: ' + what + ' spustené vnútri otvorenej '
        + 'transakcie. Vnútri db.transaction(cb) používaj tx handle (cb ho dostane), '
        + 'nie globálny db ani ďalšiu db.transaction.\n' + stack
      );
    }, WATCHDOG_MS);
    return () => clearTimeout(timer);
  };
}

function createTxRunner({ exec, makeApi, isTest }) {
  const runExclusive = createTxMutex();
  // Je práve otvorená top-level tx? (medzi BEGIN a COMMIT/ROLLBACK). Číta ho
  // watchdog — global plain op ALEBO ambientná db.transaction spustená počas
  // otvorenej tx je pod Direction A kontrakt-porušenie (self-deadlock ak volajúci
  // drží tx reťazec).
  let txOpen = false;
  const watchdog = makeDeadlockWatchdog(isTest);

  // Vyrobí SAVEPOINT-based transaction pre vnútro už otvorenej tx. `depth`
  // je lokálny per-tx-scope counter (unikátne mená sp_1, sp_2 … pri reentrancii).
  function makeSavepointTransaction() {
    let depth = 0;
    async function spTransaction(fn) {
      depth += 1;
      const sp = `sp_${depth}`;
      await exec(`SAVEPOINT ${sp};`);
      // api pre TENTO savepoint scope — jeho .transaction robí ďalší vnorený SAVEPOINT
      const innerApi = makeApi(spTransaction);
      try {
        const out = await fn(innerApi);
        await exec(`RELEASE ${sp};`);
        depth -= 1;
        return out;
      } catch (err) {
        await exec(`ROLLBACK TO ${sp};`);
        await exec(`RELEASE ${sp};`);
        depth -= 1;
        throw err;
      }
    }
    return spTransaction;
  }

  // Top-level: VŽDY cez mutex. Žiadny globálny check pred zámkom.
  async function topLevelTransaction(fn) {
    // BUG-2 poistka: ambientná db.transaction spustená POČAS otvorenej tx (napr.
    // helper otvorí vlastnú tx vnútri cudzieho callbacku) čaká na mutex, ktorý drží
    // outer tx — outer callback čaká na túto → deadlock. topLevelPrims watchdog to
    // nekryje (wrapuje len plain prims), preto ho armujeme aj tu.
    const clearWatch = txOpen ? watchdog('nested ambient db.transaction') : null;
    try {
      return await runExclusive(async () => {
        await exec('BEGIN IMMEDIATE;');
        txOpen = true;
        const spTransaction = makeSavepointTransaction();
        const txApi = makeApi(spTransaction);
        try {
          const out = await fn(txApi);
          await exec('COMMIT;');
          return out;
        } catch (err) {
          await exec('ROLLBACK;');
          throw err;
        } finally {
          txOpen = false;
        }
      });
    } finally {
      if (clearWatch) clearWatch();
    }
  }

  return { transaction: topLevelTransaction, runExclusive, isTxOpen: () => txOpen, watchdog };
}

// Zabalí top-level plain primitíva (run/all/get/exec) do zdieľaného mutexu tak,
// aby sa serializovali s top-level transakciami. Tx-scoped scope dostane priame
// (nezabalené) primitíva — bežia na už zamknutej connection, mutex = self-deadlock.
// Watchdog: global plain op spustený počas otvorenej tx = kontrakt-porušenie (viď
// makeDeadlockWatchdog); armuj timer, clear pri settle.
function topLevelPrims({ exec, run, all, get }, runExclusive, isTxOpen, watchdog) {
  const guard = (label, fn) => (sql, params) => {
    const clearWatch = (isTxOpen && isTxOpen()) ? watchdog(`global db.${label}`) : null;
    const p = runExclusive(() => fn(sql, params));
    return clearWatch ? p.finally(clearWatch) : p;
  };
  return {
    exec: guard('exec', (sql) => exec(sql)),
    run: guard('run', run),
    all: guard('all', all),
    get: guard('get', get),
  };
}

// ─── Platform detekcia ───────────────────────────────────────────────────────
// Poradie dôležité: test má prednosť pred web (jsdom je tiež "web"-like).
function detectPlatform() {
  if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'test') {
    return 'test';
  }
  try {
    // Capacitor natívna platforma (iOS/Android wrapper)
    // eslint-disable-next-line global-require
    const cap = typeof window !== 'undefined' && window.Capacitor;
    if (cap && typeof cap.isNativePlatform === 'function' && cap.isNativePlatform()) {
      return 'mobile';
    }
  } catch {
    /* ignore */
  }
  if (typeof window !== 'undefined') return 'web';
  // node mimo testu (napr. CLI script) → test driver (better-sqlite3)
  return 'test';
}

// ─── Driver: better-sqlite3 / node:sqlite (testy + node) ─────────────────────
async function makeTestDriver(filename = ':memory:') {
  let DatabaseCtor;
  let useNodeSqlite = false;
  try {
    // eslint-disable-next-line global-require
    DatabaseCtor = require('better-sqlite3');
  } catch (e) {
    // Fallback na vstavaný node:sqlite (Node 22.5+) ak better-sqlite3 native build padol.
    try {
      // eslint-disable-next-line global-require
      const ns = require('node:sqlite');
      DatabaseCtor = ns.DatabaseSync;
      useNodeSqlite = true;
    } catch {
      throw new Error('[v2/engine] žiadny node SQLite driver (better-sqlite3 ani node:sqlite): ' + e.message);
    }
  }

  const handle = new DatabaseCtor(filename);
  try {
    handle.exec('PRAGMA journal_mode = WAL;');
  } catch {
    // :memory: nepodporuje WAL → ignoruj
  }
  try {
    // Durability: NORMAL = fsync pri checkpointe (WAL-safe), :memory: ignoruje.
    handle.exec('PRAGMA synchronous = NORMAL;');
  } catch {
    /* ignore */
  }
  handle.exec('PRAGMA foreign_keys = OFF;');

  function run(sql, params = []) {
    const stmt = handle.prepare(sql);
    const info = stmt.run(...params);
    return {
      changes: Number(info.changes ?? 0),
      lastInsertRowid: info.lastInsertRowid ?? null,
    };
  }
  function all(sql, params = []) {
    const stmt = handle.prepare(sql);
    return stmt.all(...params);
  }
  function get(sql, params = []) {
    const stmt = handle.prepare(sql);
    return stmt.get(...params);
  }
  function exec(sql) {
    handle.exec(sql);
  }

  // Synchronný driver → exec je sync, ale `await` naň je no-op (resolvne hneď),
  // takže používame jednotný async tx mechanizmus ako web/mobile. Tx-scoped handle
  // namiesto globálneho txDepth (race fix): vnorenosť = volajúci drží tx api.
  const { transaction, runExclusive, isTxOpen, watchdog } = createTxRunner({
    exec,
    makeApi: (txn) => makeApi(txn, false),
    isTest: true,
  });

  // Durability: zleje -wal do hlavnej DB. Best-effort — NIKDY nehádže von
  // (:memory:/no-wal checkpoint nepodporuje).
  async function checkpoint() {
    try { handle.exec('PRAGMA wal_checkpoint(TRUNCATE);'); } catch { /* :memory:/no-wal */ }
  }

  function makeApi(txn, topLevel = false) {
    const p = topLevel ? topLevelPrims({ exec, run, all, get }, runExclusive, isTxOpen, watchdog) : { exec, run, all, get };
    return {
      platform: useNodeSqlite ? 'test:node-sqlite' : 'test:better-sqlite3',
      exec: p.exec,
      run: p.run,
      all: p.all,
      get: p.get,
      transaction: txn,
      checkpoint,
      async close() {
        // WAL sa MUSÍ zliať do hlavnej DB PRED zatvorením (data-loss guard).
        try { await checkpoint(); } catch { /* best-effort */ }
        try { handle.close(); } catch { /* ignore */ }
      },
      _raw: handle,
    };
  }

  return makeApi(transaction, true);
}

// ─── Driver: web/desktop ROUTER (DETERMINISTICKÝ — žiaden tichý backend switch) ──
// OPFS (FileSystemSyncAccessHandle) je durable a rýchle, ale beží LEN vo Web Workeri.
//
// KĽÚČOVÉ: backend sa vyberie RAZ deterministicky cez capability check a potom sa
// NEZMENÍ. Predtým tu bol try/catch čo pri OPFS worker init chybe ticho prepĺňal na
// IDB — to spôsobilo ROZKOL ÚLOŽISKA: appka písala do jednej DB, sync čítal druhú.
// Schéma: OPFS-only ak je reálne podporované; IDB len ako explicit DEGRADED režim
// pre staré prehliadače/in-app webviewy bez OPFS. Retry-once pri OPFS init (transient
// orphan-worker lock), potom HARD ERROR — appka neštartuje s rozdeleným úložiskom.
// OPFSCoopSyncVFS nepoužíva SharedArrayBuffer → žiadne COOP/COEP hlavičky.
async function makeWebDriver(dbName = 'todue_v2') {
  // 1. Capability check — deterministický boolean (reálny SyncAccessHandle test v workery).
  let opfsOk = false;
  try {
    const { isOpfsSupported } = await import('@subframe7536/sqlite-wasm');
    opfsOk = await isOpfsSupported().catch(() => false);
  } catch { opfsOk = false; }

  // 2. OPFS nie je podporované → explicit DEGRADED IDB (staré Safari/in-app webviewy).
  //    Raz vybrané, nemení sa. User to vidí: jeden console.warn pri štarte.
  if (!opfsOk) {
    console.warn('[v2/storage] backend=web:idb (DEGRADED — OPFS nedostupné; dáta môžu byť menej durable na iOS)');
    return makeIdbMainDriver(dbName);
  }

  // 3. OPFS je podporované → OPFS-only. Retry-once pri init chybe (transient orphan-worker
  //    OPFS lock sa uvoľní terminatom; reálna perzistentná nepodpora je už odfiltrovaná vyššie).
  //    Ak ani retry zlyhá → HARD ERROR. ŽIADNY tichý fallback na IDB (to bol zdroj rozdvojenia).
  let lastErr;
  for (let attempt = 1; attempt <= 2; attempt += 1) {
    try {
      const drv = await makeOpfsWorkerDriver(dbName);
      console.warn(`[v2/storage] backend=web:opfs${attempt > 1 ? ' (init retry #' + attempt + ' OK)' : ''}`);
      return drv;
    } catch (e) {
      lastErr = e;
      // Orphan worker mohol držať OPFS FileSystemSyncAccessHandle lock → reálny retry pomôže.
      // Krátka pauza nech OS uvoľní handle pred ďalším pokusom.
      if (attempt === 1) await new Promise((r) => setTimeout(r, 250));
    }
  }
  throw new Error(
    '[v2/storage] OPFS init zlyhal aj po retry ('
    + (lastErr?.message || lastErr) + '). '
    + 'Appka neštartuje s rozdeleným úložiskom — reštartuj prehliadač alebo vyčist OPFS origin.'
  );
}

// ─── Driver: OPFS vo Web Workeri (durable, main thread cez RPC) ───────────────
// Worker (sqlite.worker.js) drží jednu OPFS connection; sem posielame SQL cez postMessage.
// Worker je single-thread (FIFO) → BEGIN/INSERT/COMMIT poslané sekvenčne ostanú atomické.
async function makeOpfsWorkerDriver(dbName = 'todue_v2') {
  // workerFactory drží `new Worker(new URL(...import.meta.url))` izolovane — import.meta
  // by inak zhodil jest pri parse engine.js. Dynamický import → v testoch sa nenačíta.
  const { createSqliteWorker } = await import('./workerFactory');
  const worker = createSqliteWorker();
  let _seq = 0;
  const pending = new Map();
  // #8: po onerror je worker mŕtvy — ďalšie rpc() musia okamžite zlyhať (nie visieť
  // navždy v pending bez timeoutu → zamrznuté UI/save). Dead-flag je deterministický:
  // žiadne false timeouty pri pomalých legitímnych queries.
  let _dead = null;

  worker.onmessage = (ev) => {
    const { id, ok, error, ...rest } = ev.data || {};
    const p = pending.get(id);
    if (!p) return;
    pending.delete(id);
    if (ok) p.resolve(rest); else p.reject(new Error(error || 'worker error'));
  };
  worker.onerror = (e) => {
    const err = new Error('[v2] sqlite worker zlyhal: ' + (e?.message || 'unknown'));
    _dead = err; // označ driver ako mŕtvy → ďalšie rpc() reject okamžite
    for (const p of pending.values()) p.reject(err);
    pending.clear();
  };

  function rpc(method, sql, params, extra) {
    if (_dead) return Promise.reject(_dead);
    return new Promise((resolve, reject) => {
      const id = ++_seq;
      pending.set(id, { resolve, reject });
      worker.postMessage({ id, method, sql, params, ...extra });
    });
  }

  // init — wa-sqlite.wasm = SYNC build (OPFS). Timeout aby fallback nezamrzol.
  const base = (typeof process !== 'undefined' && process.env && process.env.PUBLIC_URL) || '';
  try {
    await Promise.race([
      rpc('init', null, null, { dbName, url: `${base}/wa-sqlite.wasm` }),
      new Promise((_, rej) => setTimeout(() => rej(new Error('OPFS init timeout 8s')), 8000)),
    ]);
  } catch (e) {
    // #9: pri init timeoute/zlyhaní MUSÍME worker terminovať pred fallbackom na IDB,
    // inak osirelý worker drží OPFS FileSystemSyncAccessHandle lock → ďalší štart
    // zlyhá na locku + dáta rozdelené medzi OPFS a IDB.
    try { worker.terminate(); } catch { /* ignore */ }
    throw e;
  }

  // Durability: NORMAL = fsync pri checkpointe (WAL-safe). Best-effort cez rovnaký
  // rpc mechanizmus akým idú ostatné pragmy/SQL do workera.
  try { await rpc('exec', 'PRAGMA synchronous = NORMAL;'); } catch { /* best-effort */ }

  async function exec(sql) { return rpc('exec', sql); }
  async function all(sql, params = []) { const r = await rpc('all', sql, params); return r.rows || []; }
  async function get(sql, params = []) { const r = await rpc('get', sql, params); return r.row; }
  async function run(sql, params = []) {
    const r = await rpc('run', sql, params);
    return { changes: Number(r.changes ?? 0), lastInsertRowid: r.lastId ?? null };
  }

  // Tx-scoped handle (race fix): top-level vždy cez mutex, vnorené cez SAVEPOINT
  // na odovzdanom tx api — nie cez globálny txDepth čítaný cez await hranicu.
  const { transaction, runExclusive, isTxOpen, watchdog } = createTxRunner({ exec, makeApi: (txn) => makeApi(txn, false), isTest: false });

  // Durability: zleje -wal do hlavnej OPFS DB. Best-effort — NIKDY nehádže von.
  async function checkpoint() {
    try { await rpc('exec', 'PRAGMA wal_checkpoint(TRUNCATE);'); } catch { /* best-effort */ }
  }

  function makeApi(txn, topLevel = false) {
    const p = topLevel ? topLevelPrims({ exec, run, all, get }, runExclusive, isTxOpen, watchdog) : { exec, run, all, get };
    return {
      platform: 'web:opfs',
      exec: p.exec,
      run: p.run,
      all: p.all,
      get: p.get,
      transaction: txn,
      checkpoint,
      async close() {
        // WAL sa MUSÍ zliať do hlavnej DB PRED terminate (data-loss guard).
        try { await checkpoint(); } catch { /* best-effort */ }
        try { worker.terminate(); } catch { /* ignore */ }
      },
      _raw: worker,
    };
  }

  return makeApi(transaction, true);
}

// ─── Driver: web/desktop IDB fallback (wa-sqlite + IndexedDB, MAIN thread) ─────
// Beží keď OPFS nie je dostupné. API: initSQLite(storage) → { run, changes, lastInsertRowId }.
// changes()/lastInsertRowId() natívne. useIdbStorage v podceste '/idb'. wasm-async z craco.
async function makeIdbMainDriver(dbName = 'todue_v2') {
  const { initSQLite } = await import('@subframe7536/sqlite-wasm');
  // Alias bez 'use' prefixu — react-hooks/rules-of-hooks by useIdbStorage mylne
  // pokladal za React Hook a zhodil by build (volá sa mimo komponentu).
  const { useIdbStorage: openIdbStorage } = await import('@subframe7536/sqlite-wasm/idb');

  const base = (typeof process !== 'undefined' && process.env && process.env.PUBLIC_URL) || '';
  const url = `${base}/wa-sqlite-async.wasm`;
  const sqlite = await initSQLite(openIdbStorage(dbName, { url }));

  function exec(sql) { return sqlite.run(sql); }

  // Durability: NORMAL = fsync pri checkpointe (WAL-safe). Best-effort cez rovnaký
  // exec mechanizmus akým idú ostatné pragmy/SQL.
  try { await exec('PRAGMA synchronous = NORMAL;'); } catch { /* best-effort */ }
  async function all(sql, params = []) { return sqlite.run(sql, params); }
  async function get(sql, params = []) {
    const rows = await sqlite.run(sql, params);
    return rows && rows[0];
  }
  async function run(sql, params = []) {
    await sqlite.run(sql, params);
    // natívne changes()/lastInsertRowId() z balíka — bez race (žiadny separátny SELECT).
    return { changes: Number(sqlite.changes() ?? 0), lastInsertRowid: sqlite.lastInsertRowId?.() ?? null };
  }

  // Tx-scoped handle (race fix): top-level vždy cez mutex, vnorené cez SAVEPOINT
  // na odovzdanom tx api — nie cez globálny txDepth čítaný cez await hranicu.
  const { transaction, runExclusive, isTxOpen, watchdog } = createTxRunner({ exec, makeApi: (txn) => makeApi(txn, false), isTest: false });

  // Durability: zleje -wal do hlavnej DB. Best-effort — NIKDY nehádže von.
  async function checkpoint() {
    try { await exec('PRAGMA wal_checkpoint(TRUNCATE);'); } catch { /* best-effort */ }
  }

  function makeApi(txn, topLevel = false) {
    const p = topLevel ? topLevelPrims({ exec, run, all, get }, runExclusive, isTxOpen, watchdog) : { exec, run, all, get };
    return {
      platform: 'web:idb', // IDB storage (main thread); OPFS durability doladíme neskôr
      exec: p.exec,
      run: p.run,
      all: p.all,
      get: p.get,
      transaction: txn,
      checkpoint,
      async close() {
        // WAL sa MUSÍ zliať do hlavnej DB PRED zatvorením (data-loss guard).
        try { await checkpoint(); } catch { /* best-effort */ }
        try { sqlite.close?.(); } catch { /* ignore */ }
      },
      _raw: sqlite,
    };
  }

  return makeApi(transaction, true);
}

// ─── Driver: mobil (@capacitor-community/sqlite, natívny) ─────────────────────
// Lazy import — beží len v Capacitor wrapperi. V teste sa NIKDY nezavolá.
async function makeMobileDriver(dbName = 'todue_v2') {
  const { CapacitorSQLite, SQLiteConnection } = await import('@capacitor-community/sqlite');
  const conn = new SQLiteConnection(CapacitorSQLite);
  const handle = await conn.createConnection(dbName, false, 'no-encryption', 1, false);
  await handle.open();

  async function exec(sql) { await handle.execute(sql); }
  async function all(sql, params = []) {
    const res = await handle.query(sql, params);
    return res.values || [];
  }
  async function get(sql, params = []) {
    const rows = await all(sql, params);
    return rows[0];
  }
  async function run(sql, params = []) {
    const res = await handle.run(sql, params, false);
    return {
      changes: Number(res?.changes?.changes ?? 0),
      lastInsertRowid: res?.changes?.lastId ?? null,
    };
  }

  // Tx-scoped handle (race fix): top-level vždy cez mutex, vnorené cez SAVEPOINT
  // na odovzdanom tx api — nie cez globálny txDepth čítaný cez await hranicu.
  const { transaction, runExclusive, isTxOpen, watchdog } = createTxRunner({ exec, makeApi: (txn) => makeApi(txn, false), isTest: false });

  // Durability: zleje -wal do hlavnej DB. Best-effort — NIKDY nehádže von.
  async function checkpoint() {
    try { await exec('PRAGMA wal_checkpoint(TRUNCATE);'); } catch { /* best-effort */ }
  }

  function makeApi(txn, topLevel = false) {
    const p = topLevel ? topLevelPrims({ exec, run, all, get }, runExclusive, isTxOpen, watchdog) : { exec, run, all, get };
    return {
      platform: 'mobile:capacitor',
      exec: p.exec,
      run: p.run,
      all: p.all,
      get: p.get,
      transaction: txn,
      checkpoint,
      async close() {
        // WAL sa MUSÍ zliať do hlavnej DB PRED zatvorením (data-loss guard).
        try { await checkpoint(); } catch { /* best-effort */ }
        try { handle.close?.(); } catch { /* ignore */ }
      },
      _raw: handle,
    };
  }

  return makeApi(transaction, true);
}

// ─── Platform router + lazy singleton ────────────────────────────────────────
async function makeDriver(opts = {}) {
  const platform = opts.platform || detectPlatform();
  switch (platform) {
    case 'mobile': return makeMobileDriver(opts.dbName);
    case 'web': return makeWebDriver(opts.dbName);
    case 'test':
    default: return makeTestDriver(opts.filename || ':memory:');
  }
}

// Singleton MUSÍ byť GLOBÁLNY, nie modulový. `engine.js` sa cez webpack
// code-splitting zduplikuje do viacerých chunkov (UI stránky vs driveSync) — každá
// kópia má vlastný modulový scope, takže modulové `_engine` = DVA engine → DVE
// fyzické DB (OPFS vs IDB fallback po OPFS locku). UI píše do jednej, sync číta
// druhú → faktúry sa nikdy nezosynchronizujú. Global slot zdieľa jeden engine
// (a jeden in-flight promise) naprieč VŠETKÝMI kópiami modulu + serializuje súbežné
// otvorenia (race fix). currentEngine číta ten istý slot.
const _G = (typeof globalThis !== 'undefined' && globalThis)
  || (typeof self !== 'undefined' && self)
  || (typeof window !== 'undefined' && window)
  || {};
const _SLOT = '__todueV2EngineSingleton__';
if (!_G[_SLOT]) _G[_SLOT] = { engine: null, promise: null };

// Otvorí (alebo vráti) jedinú DB connection na celý život appky.
export async function getEngine(opts = {}) {
  const slot = _G[_SLOT];
  if (slot.engine) return slot.engine;
  if (!slot.promise) {
    slot.promise = makeDriver(opts).then(
      (e) => {
        slot.engine = e;
        return e;
      },
      (err) => { slot.promise = null; throw err; }, // zlyhanie → dovoľ retry
    );
  }
  return slot.promise;
}

// Testovacie/explicitné resetnutie singletonu (čerstvá :memory: DB per suite).
export async function resetEngine(opts = {}) {
  const slot = _G[_SLOT];
  if (slot.engine) {
    // close() je async (checkpoint pred teardown) → MUSÍME await, inak starý OPFS
    // worker ešte drží FileSystemSyncAccessHandle lock keď otvárame nový engine →
    // init nového workera zlyhá na locku (orphan-worker race, viď #9 vyššie).
    try { await slot.engine.close(); } catch { /* ignore */ }
  }
  slot.engine = await makeDriver(opts);
  slot.promise = Promise.resolve(slot.engine);
  return slot.engine;
}

export function currentEngine() {
  return _G[_SLOT].engine;
}

export { detectPlatform };
